/**
 * Itinerary optimizer for the Plan page.
 *
 * Pure client-side. Given the saved events, the user's origin, and the
 * wizard timeframe, produces an order + day split + drive-time chips.
 *
 * Algorithm (kept intentionally simple — N ≤ ~20 typical saves):
 *   1. Build pairwise drive-time matrix (origin + each event), using
 *      event lat/lng with ZIP centroid fallback (same tiering as
 *      `driveMinutesFor` on Explore).
 *   2. Decide day count from dwell + a nearest-neighbor tour estimate,
 *      clamped to the wizard's timeframe length.
 *   3. Greedy geographic cluster into days, with soft tie-breakers for
 *      schedule open-days, pinned specific dates, and weather sensitivity.
 *   4. Within each day, order via nearest-neighbor + one 2-opt pass.
 *      Soft morning nudge for high-weather-sensitivity outdoor stops.
 *   5. Schedule clock times starting at 9:00 AM.
 */

import {
  haversineMiles,
  estimateDriveMinutes,
  extractZipFromAddress,
  LWR_CENTER,
  type LatLng,
} from "@/lib/distance";
import { parseDurationHours } from "@/lib/wizard-types";
import {
  getOpenWeekdays,
  extractSpecificDates,
  datesInRange,
} from "@/lib/event-schedule";

export interface OptimizableEvent {
  id: string;
  estimated_duration: string;
  weather_sensitivity?: number;
  indoor_outdoor?: string;
  schedule_notes?: string;
  start_date?: string | null;
  end_date?: string | null;
  lat?: number | null;
  lng?: number | null;
  venue_address?: string;
}

export interface ItineraryStop<E> {
  event: E;
  driveFromPrevMin: number;
  dwellMin: number;
  arriveMin: number; // minutes since midnight of that day
  departMin: number;
}

export interface ItineraryDay<E> {
  label: string;
  dateISO?: string;
  weekday?: number;
  stops: ItineraryStop<E>[];
  driveHomeMin: number;
  totalActiveMin: number; // dwell + drives (excluding drive home)
}

export interface OptimizedItinerary<E> {
  days: ItineraryDay<E>[];
  totalDriveMin: number;
  origin: LatLng;
  tightFit?: boolean;
}

const DAILY_BUDGET_MIN = 540; // 9 hrs
const DAY_START_MIN = 9 * 60;
const MULTI_DAY_CAP_MIN = 6 * 60; // treat "Multiple days" stops as 6h for math

const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function eventCoords(e: OptimizableEvent, zipCoords?: Map<string, LatLng>): LatLng {
  if (typeof e.lat === "number" && typeof e.lng === "number") {
    return { lat: e.lat, lng: e.lng };
  }
  const zip = extractZipFromAddress(e.venue_address);
  if (zip && zipCoords) {
    const c = zipCoords.get(zip);
    if (c) return c;
  }
  return LWR_CENTER;
}

function driveMin(a: LatLng, b: LatLng): number {
  if (a.lat === b.lat && a.lng === b.lng) return 0;
  return estimateDriveMinutes(haversineMiles(a, b));
}

function dwellMinFor(e: OptimizableEvent): number {
  const t = (e.estimated_duration || "").toLowerCase();
  if (/multiple days|all day|whole day/.test(t)) return MULTI_DAY_CAP_MIN;
  const { min, max } = parseDurationHours(e.estimated_duration || "");
  const mid = ((min + max) / 2) * 60;
  return Math.max(30, Math.round(mid));
}

function nearestNeighborOrder(
  ids: number[],
  costs: number[][],
  startIdx: number,
): number[] {
  const remaining = new Set(ids);
  const order: number[] = [];
  let cur = startIdx;
  while (remaining.size > 0) {
    let bestId = -1;
    let bestCost = Infinity;
    for (const id of remaining) {
      const c = costs[cur][id];
      if (c < bestCost) {
        bestCost = c;
        bestId = id;
      }
    }
    order.push(bestId);
    remaining.delete(bestId);
    cur = bestId;
  }
  return order;
}

function tourLen(seq: number[], costs: number[][], startIdx: number): number {
  if (seq.length === 0) return 0;
  let total = costs[startIdx][seq[0]];
  for (let i = 0; i < seq.length - 1; i++) total += costs[seq[i]][seq[i + 1]];
  return total;
}

function twoOpt(seq: number[], costs: number[][], startIdx: number): number[] {
  if (seq.length < 4) return seq;
  let best = seq.slice();
  let bestLen = tourLen(best, costs, startIdx);
  let improved = true;
  let guard = 0;
  while (improved && guard++ < 20) {
    improved = false;
    for (let i = 0; i < best.length - 1; i++) {
      for (let j = i + 1; j < best.length; j++) {
        const next = best.slice(0, i).concat(best.slice(i, j + 1).reverse(), best.slice(j + 1));
        const len = tourLen(next, costs, startIdx);
        if (len + 0.01 < bestLen) {
          best = next;
          bestLen = len;
          improved = true;
        }
      }
    }
  }
  return best;
}

function daysBetween(startISO: string, endISO: string): number {
  if (!startISO || !endISO) return 0;
  const s = Date.parse(startISO);
  const e = Date.parse(endISO);
  if (!Number.isFinite(s) || !Number.isFinite(e)) return 0;
  return Math.max(1, Math.round((e - s) / 86400000) + 1);
}

function isoForDayOffset(startISO: string, offset: number): string | undefined {
  if (!startISO) return undefined;
  const s = Date.parse(startISO);
  if (!Number.isFinite(s)) return undefined;
  const d = new Date(s + offset * 86400000);
  return d.toISOString().slice(0, 10);
}

function weekdayForISO(iso?: string): number | undefined {
  if (!iso) return undefined;
  const d = Date.parse(iso + "T12:00:00Z");
  if (!Number.isFinite(d)) return undefined;
  return new Date(d).getUTCDay();
}

function formatDateLabel(iso?: string): string {
  if (!iso) return "";
  const d = Date.parse(iso + "T12:00:00Z");
  if (!Number.isFinite(d)) return "";
  const dt = new Date(d);
  const wd = WEEKDAY_NAMES[dt.getUTCDay()];
  const mo = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][dt.getUTCMonth()];
  return `${wd} ${mo} ${dt.getUTCDate()}`;
}

export function optimizeItinerary<E extends OptimizableEvent>(
  events: E[],
  origin: LatLng | null,
  timeframe: { start?: string; end?: string } | null,
  zipCoords?: Map<string, LatLng>,
): OptimizedItinerary<E> {
  const start = origin ?? LWR_CENTER;
  const N = events.length;

  if (N === 0) {
    return { days: [], totalDriveMin: 0, origin: start };
  }

  // Coords + costs (index 0 = origin, 1..N = events)
  const coords: LatLng[] = [start, ...events.map((e) => eventCoords(e, zipCoords))];
  const costs: number[][] = Array.from({ length: N + 1 }, () => new Array(N + 1).fill(0));
  for (let i = 0; i <= N; i++) {
    for (let j = i + 1; j <= N; j++) {
      const d = driveMin(coords[i], coords[j]);
      costs[i][j] = d;
      costs[j][i] = d;
    }
  }

  const dwells = events.map(dwellMinFor);
  const idsAll = events.map((_, i) => i + 1);

  // Quick tour estimate to decide day count
  const tourSeq = nearestNeighborOrder(idsAll, costs, 0);
  const roughTour = tourLen(tourSeq, costs, 0);
  const totalDwell = dwells.reduce((a, b) => a + b, 0);
  let dayCount = Math.max(1, Math.ceil((totalDwell + roughTour) / DAILY_BUDGET_MIN));

  // Bound by wizard timeframe length when present.
  const windowLen = daysBetween(timeframe?.start || "", timeframe?.end || "");
  let tightFit = false;
  if (windowLen > 0 && dayCount > windowLen) {
    dayCount = windowLen;
    tightFit = true;
  }

  // Pin specific-date events to their day index when possible.
  const pins = new Map<number, number>(); // eventIdx (1-based) -> dayIdx
  if (timeframe?.start && timeframe?.end && windowLen > 0) {
    for (let i = 0; i < N; i++) {
      const dates = extractSpecificDates(events[i].schedule_notes);
      if (dates.length < 2) continue;
      const hits = datesInRange(dates, timeframe.start, timeframe.end);
      if (hits.length === 0) continue;
      const dayIdx = Math.min(
        dayCount - 1,
        Math.max(0, Math.round((Date.parse(hits[0]) - Date.parse(timeframe.start)) / 86400000)),
      );
      pins.set(i + 1, dayIdx);
    }
  }

  // Assign events to day buckets.
  const buckets: number[][] = Array.from({ length: dayCount }, () => []);
  const dayLoad: number[] = new Array(dayCount).fill(0);

  // Apply pins first.
  for (const [evId, dayIdx] of pins) {
    buckets[dayIdx].push(evId);
    dayLoad[dayIdx] += dwells[evId - 1];
  }

  // Seed each remaining empty day with the farthest unassigned event from origin
  // (encourages geographic spread across days).
  const unassigned = new Set(idsAll.filter((id) => !pins.has(id)));
  for (let d = 0; d < dayCount; d++) {
    if (buckets[d].length > 0) continue;
    let bestId = -1;
    let bestD = -1;
    for (const id of unassigned) {
      if (costs[0][id] > bestD) {
        bestD = costs[0][id];
        bestId = id;
      }
    }
    if (bestId > 0) {
      buckets[d].push(bestId);
      dayLoad[d] += dwells[bestId - 1];
      unassigned.delete(bestId);
    }
  }

  // For each remaining event, pick the day with the lowest combined score of
  // (closest seed/centroid + lightest load + soft constraints).
  const remaining = [...unassigned].sort((a, b) => costs[0][b] - costs[0][a]);
  for (const id of remaining) {
    let bestDay = 0;
    let bestScore = Infinity;
    for (let d = 0; d < dayCount; d++) {
      // Min drive to anything already in this day's bucket.
      let minDrive = costs[0][id];
      for (const other of buckets[d]) {
        const v = costs[id][other];
        if (v < minDrive) minDrive = v;
      }
      // Soft penalty if day is already overbooked.
      const overflow = Math.max(0, dayLoad[d] + dwells[id - 1] - DAILY_BUDGET_MIN);
      // Soft penalty if event's open-weekdays don't include this day.
      const openSet = getOpenWeekdays(events[id - 1].schedule_notes);
      const wd = weekdayForISO(isoForDayOffset(timeframe?.start || "", d));
      const closedPenalty = openSet && wd !== undefined && !openSet.has(wd) ? 120 : 0;
      // Weather-sensitive items prefer earlier days.
      const ws = events[id - 1].weather_sensitivity ?? 0;
      const wxPenalty = ws >= 70 ? d * 8 : 0;

      const score = minDrive + overflow * 0.5 + closedPenalty + wxPenalty;
      if (score < bestScore) {
        bestScore = score;
        bestDay = d;
      }
    }
    buckets[bestDay].push(id);
    dayLoad[bestDay] += dwells[id - 1];
  }

  // Order within each day; build clock-time schedule.
  const days: ItineraryDay<E>[] = [];
  let totalDrive = 0;

  for (let d = 0; d < dayCount; d++) {
    const bucket = buckets[d];
    if (bucket.length === 0) continue;

    let order = nearestNeighborOrder(bucket, costs, 0);
    order = twoOpt(order, costs, 0);

    // Soft morning nudge: swap adjacent pairs if the later one is high-wx
    // outdoor and the earlier one isn't, and the swap doesn't blow up drive.
    const baseLen = tourLen(order, costs, 0);
    for (let i = 0; i < order.length - 1; i++) {
      const a = events[order[i] - 1];
      const b = events[order[i + 1] - 1];
      const aWx = (a.weather_sensitivity ?? 0) >= 70 && a.indoor_outdoor !== "indoor";
      const bWx = (b.weather_sensitivity ?? 0) >= 70 && b.indoor_outdoor !== "indoor";
      if (!aWx && bWx) {
        const swapped = order.slice();
        [swapped[i], swapped[i + 1]] = [swapped[i + 1], swapped[i]];
        const newLen = tourLen(swapped, costs, 0);
        if (newLen <= baseLen * 1.1) order = swapped;
      }
    }

    const stops: ItineraryStop<E>[] = [];
    let prev = 0;
    let clock = DAY_START_MIN;
    for (const id of order) {
      const drive = costs[prev][id];
      const dwell = dwells[id - 1];
      const arrive = clock + drive;
      const depart = arrive + dwell;
      stops.push({
        event: events[id - 1],
        driveFromPrevMin: drive,
        dwellMin: dwell,
        arriveMin: arrive,
        departMin: depart,
      });
      totalDrive += drive;
      clock = depart;
      prev = id;
    }
    const driveHome = costs[prev][0];
    totalDrive += driveHome;

    const iso = isoForDayOffset(timeframe?.start || "", d);
    const wd = weekdayForISO(iso);
    const activeMin = stops.reduce((s, st) => s + st.driveFromPrevMin + st.dwellMin, 0);
    days.push({
      label: dayCount === 1 ? "Your day" : `Day ${days.length + 1}`,
      dateISO: iso,
      weekday: wd,
      stops,
      driveHomeMin: driveHome,
      totalActiveMin: activeMin,
    });
  }

  return { days, totalDriveMin: totalDrive, origin: start, tightFit };
}

/** Format a minutes-since-midnight value as a 12-hour clock string. */
export function formatClock(min: number): string {
  const total = Math.round(min);
  const h24 = Math.floor(total / 60) % 24;
  const m = total % 60;
  const h12 = ((h24 + 11) % 12) + 1;
  const ampm = h24 < 12 ? "am" : "pm";
  return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, "0")}${ampm}`;
}

/** Format a minute duration as "1h 25m" / "45 min". */
export function formatDuration(min: number): string {
  const m = Math.max(0, Math.round(min));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r === 0 ? `${h}h` : `${h}h ${r}m`;
}

/** Flatten an OptimizedItinerary back into a plain ordered list of events. */
export function flattenOrder<E>(it: OptimizedItinerary<E>): E[] {
  return it.days.flatMap((d) => d.stops.map((s) => s.event));
}